// ─────────────────────────────────────────────
//  PercentageSlider.jsx — Slider 0–100% + input manual
// ─────────────────────────────────────────────

import React, { useRef } from 'react';
import styles from './PercentageSlider.module.css';

function formatPctDisplay(value) {
	if (value === '' || value === '.') return value;
	const num = parseFloat(value);
	if (isNaN(num)) return '';
	if (Number.isInteger(num)) return String(num);
	return parseFloat(num.toFixed(2)).toString();
}

const SLIDER_MAX = 100;
const INPUT_MAX = 999;

export default function PercentageSlider({
	value,
	onChange,
	min = 0,
	max = SLIDER_MAX,
	inputMax = INPUT_MAX,
	quickPcts,
	adjustmentAmount,
	mode,
}) {
	const inputRef = useRef(null);
	const numericValue = parseFloat(value) || 0;
	const sliderValue = Math.min(max, Math.max(min, numericValue));
	const fillPct = ((sliderValue - min) / (max - min)) * 100;

	const handleSliderChange = (e) => {
		onChange(String(Number(e.target.value)));
	};

	const handleInputChange = (e) => {
		const raw = e.target.value;
		const sanitized = raw.replace(/[^0-9.]/g, '');
		const parts = sanitized.split('.');
		if (parts.length > 2) return;
		if (parts[1] !== undefined && parts[1].length > 2) return;

		if (sanitized !== '' && sanitized !== '.') {
			const num = parseFloat(sanitized);
			if (!isNaN(num) && num > inputMax) {
				onChange(String(inputMax));
				return;
			}
		}

		onChange(sanitized);
	};

	const handleInputBlur = () => {
		if (value === '' || value === '.') {
			onChange('0');
			return;
		}
		const num = parseFloat(value);
		if (isNaN(num)) {
			onChange('0');
			return;
		}
		const clamped = Math.min(inputMax, Math.max(min, num));
		onChange(formatPctDisplay(clamped));
	};

	const modeLabel = mode === 'discount' ? 'Descuento' : 'Aumento';
	const inputWidthCh = Math.min(6, Math.max(2, String(value).length || 1));
	const sliderAriaValueText = numericValue > max
		? `${numericValue} por ciento (slider al máximo de ${max}%)`
		: `${sliderValue} por ciento`;

	const focusPctInput = () => {
		inputRef.current?.focus({ preventScroll: true });
		inputRef.current?.select();
	};

	return (
		<section className={styles.card} aria-label="Porcentaje">
			<div className={styles.header}>
				
				<div>
					<p className={styles.label}>{modeLabel}</p>
					<div className={styles.pctInputWrap}>
						<input
							ref={inputRef}
							className={styles.pctInput}
							type="text"
							inputMode="decimal"
							value={value}
							style={{ width: `${inputWidthCh}ch` }}
							aria-label={`Porcentaje de ${modeLabel.toLowerCase()}, de 0 a ${inputMax}`}
							onChange={handleInputChange}
							onBlur={handleInputBlur}
						/>
						<span className={styles.pctSign} aria-hidden="true">%</span>
					</div>
				</div>

				<button
					type="button"
					className={styles.adjustmentHint}
					onClick={focusPctInput}
					aria-label="Editar porcentaje manualmente"
				>
					Ó da click en valor para editar
				</button>

				{/* <p className={styles.adjustmentHint} aria-live="polite">
					{adjustmentAmount !== null ? (
						<>
							<strong>{adjustmentAmount}</strong>
							{' '}
							{mode === 'discount' ? 'menos' : 'más'}
						</>
					) : (
						'—'
					)}
				</p> */}
			</div>

			<div className={styles.trackWrap}>
				
				<div className={styles.trackBg}>
					<div className={styles.trackFill} style={{ width: `${fillPct}%` }} />
				</div>

				<input
					type="range"
					className={styles.slider}
					min={min}
					max={max}
					step={0.1}
					value={sliderValue}
					aria-label={`Porcentaje de ${modeLabel.toLowerCase()}, máximo ${max}%`}
					aria-valuemin={min}
					aria-valuemax={max}
					aria-valuenow={sliderValue}
					aria-valuetext={sliderAriaValueText}
					onChange={handleSliderChange}
				/>
			</div>

			{/* <div className={styles.axisLabels} aria-hidden="true">
				<span>0%</span>
				<span>50%</span>
				<span>100%</span>
			</div> */}

			<div
				className={styles.quickBtns}
				role="group"
				aria-label="Porcentajes de acceso rápido"
			>
				{quickPcts.map((pct) => {
					const isActive = numericValue === pct;
					return (
						<button
							key={pct}
							type="button"
							className={`${styles.quickBtn} ${isActive ? styles.quickBtnActive : ''}`}
							onClick={() => onChange(String(pct))}
							aria-pressed={isActive}
							aria-label={`${pct} por ciento`}
						>
							{pct}%
						</button>
					);
				})}
			</div>
		</section>
	);
}
